import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalDate } from "@js-joda/core";
import {
  SegmentController,
  SegmentOption,
} from "components/designV2/segmentcontroller/segmentcontroller";
import { isSettingEnabled } from "model/settings.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { AGIOverview } from "./agiOverview.component";
import { GenerateSalaryNoticesModal } from "./generateSalaryNoticesModal.component";
import { IndividualSalary } from "./individualSalary.component";
import { MonthlySalary } from "./montlySalary/monthlySalary.component";
import { MonthOverview } from "./montlySalary/monthOverview.component";
import { SalaryExportsOverview } from "./salaryExports.component";
import { SalaryTabsHeader } from "./salary.styled";
import { useGenerateSalaryNoticesModal } from "./useGenerateSalaryNoticesModal";

type SalaryTabKey =
  | "MonthOverview"
  | "Monthly"
  | "Individual"
  | "AGI"
  | "Exports";

export const SalaryTabs: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<SalaryTabKey>("MonthOverview");
  const [monthlySalaryDate, setMonthlySalaryDate] = useState(LocalDate.now());
  const [monthlySalaryRefreshKey, setMonthlySalaryRefreshKey] = useState(0);
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const generateSalaryNoticesModal = useGenerateSalaryNoticesModal({
    companyId,
    companySettings: companyState.companySettings,
    currentDate: monthlySalaryDate,
    onCreated: () => setMonthlySalaryRefreshKey((refreshKey) => refreshKey + 1),
  });

  const haveSalaryModule = useMemo(() => {
    return isSettingEnabled(
      companyState.companySettings,
      "EnableCompanySalaryView",
    );
  }, [companyState.companySettings]);

  const options = useMemo<SegmentOption<SalaryTabKey>[]>(() => {
    const base: SegmentOption<SalaryTabKey>[] = [
      { value: "MonthOverview", label: t("Salary.Salaries") },
    ];
    if (haveSalaryModule) {
      base.push(
        { value: "Monthly", label: t("Salary.MonthlySalary") },
        { value: "Individual", label: t("Salary.IndividualSalary") },
        { value: "AGI", label: t("Salary.AGI") },
      );
    } else {
      base.push({ value: "Exports", label: t("Salary.SalaryExports") });
    }
    return base;
  }, [haveSalaryModule, t]);

  return (
    <>
      <SalaryTabsHeader>
        <SegmentController
          options={options}
          value={currentTab}
          onChange={setCurrentTab}
        />
      </SalaryTabsHeader>
      {currentTab === "MonthOverview" && <MonthOverview />}
      {currentTab === "Monthly" && (
        <MonthlySalary
          currentDate={monthlySalaryDate}
          onOpenGenerateSalaryNoticesModal={
            generateSalaryNoticesModal.openModal
          }
          refreshKey={monthlySalaryRefreshKey}
          setCurrentDate={setMonthlySalaryDate}
        />
      )}
      {currentTab === "Individual" && <IndividualSalary />}
      {currentTab === "AGI" && <AGIOverview />}
      {currentTab === "Exports" && <SalaryExportsOverview />}
      <GenerateSalaryNoticesModal
        avatarUrls={generateSalaryNoticesModal.avatarUrls}
        canCreateSalaryNotices={
          generateSalaryNoticesModal.canCreateSalaryNotices
        }
        cutOffDate={generateSalaryNoticesModal.cutOffDate}
        generateSalaryNoticesMode={
          generateSalaryNoticesModal.generateSalaryNoticesMode
        }
        isGeneratingSalaryNotices={
          generateSalaryNoticesModal.isGeneratingSalaryNotices
        }
        isCutOffWarningOpen={generateSalaryNoticesModal.isCutOffWarningOpen}
        isOpen={generateSalaryNoticesModal.isOpen}
        modalState={generateSalaryNoticesModal.modalState}
        salaryNoticeWorkers={generateSalaryNoticesModal.salaryNoticeWorkers}
        selectedWorkerIds={generateSalaryNoticesModal.selectedWorkerIds}
        onClose={generateSalaryNoticesModal.closeModal}
        onCancelCutOffWarning={generateSalaryNoticesModal.cancelCutOffWarning}
        onCreate={generateSalaryNoticesModal.createSalaryNotices}
        onConfirmCreate={generateSalaryNoticesModal.confirmCreateSalaryNotices}
        onModeChange={
          generateSalaryNoticesModal.handleGenerateSalaryNoticesModeChange
        }
        onToggleWorker={generateSalaryNoticesModal.toggleSelectedWorker}
        onWorkerActionClick={generateSalaryNoticesModal.handleWorkerActionClick}
      />
    </>
  );
};
